import { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import type { OcdDetailsQuestionTemplate } from '../services/settingsTypes';

interface OcdDetailsQuestionEditModalProps {
    template: OcdDetailsQuestionTemplate | null;
    isNew: boolean;
    onSave: (template: OcdDetailsQuestionTemplate, isNew: boolean) => Promise<void>;
    onClose: () => void;
}

export const OcdDetailsQuestionEditModal = ({
    template,
    isNew,
    onSave,
    onClose,
}: OcdDetailsQuestionEditModalProps) => {
    const [questionTemplate, setQuestionTemplate] = useState('');
    const [questionContext, setQuestionContext] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [ocdProfileKey, setOcdProfileKey] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (template) {
            setQuestionTemplate(template.questionTemplate);
            setQuestionContext(template.questionContext);
            setSubtitle(template.subtitle);
            setOcdProfileKey(template.ocdProfileKey);
        } else {
            setQuestionTemplate('');
            setQuestionContext('');
            setSubtitle('');
            setOcdProfileKey('');
        }
    }, [template]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validation
        if (!questionTemplate.trim()) {
            setError('Question template is required');
            return;
        }

        if (!questionContext.trim()) {
            setError('Question context is required');
            return;
        }

        if (!subtitle.trim()) {
            setError('Subtitle is required');
            return;
        }

        if (!ocdProfileKey.trim()) {
            setError('OCD Profile Key is required');
            return;
        }

        // Validate ocdProfileKey format (lowercase letters, numbers, and underscores only)
        if (!/^[a-z0-9_]+$/.test(ocdProfileKey)) {
            setError('OCD Profile Key must contain only lowercase letters, numbers, and underscores');
            return;
        }

        const templateData: OcdDetailsQuestionTemplate = {
            questionTemplate: questionTemplate.trim(),
            questionContext: questionContext.trim(),
            subtitle: subtitle.trim(),
            ocdProfileKey: ocdProfileKey.trim(),
            order: template?.order || 0,
        };

        try {
            setIsSaving(true);
            await onSave(templateData, isNew);
        } catch (err) {
            console.error('Failed to save question template:', err);
            setError(err instanceof Error ? err.message : 'Failed to save question template');
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isNew ? 'Add New Question Template' : 'Edit Question Template'}
                    </h3>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label htmlFor="questionTemplate" className="block text-sm font-medium text-gray-700 mb-1">
                            Question Template *
                        </label>
                        <textarea
                            id="questionTemplate"
                            value={questionTemplate}
                            onChange={(e) => setQuestionTemplate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., What is the theme that is bothering you the most right now?"
                            rows={3}
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            The question text. Use {'{previous answer}'} to reference the previous answer.
                        </p>
                    </div>

          <div>
            <label htmlFor="questionContext" className="block text-sm font-medium text-gray-700 mb-1">
              Question Context *
            </label>
            <textarea
              id="questionContext"
              value={questionContext}
              onChange={(e) => setQuestionContext(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., onboarding_most_distressing_theme"
              rows={3}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Internal context identifier used for AI generation.
            </p>
          </div>

                    <div>
                        <label htmlFor="subtitle" className="block text-sm font-medium text-gray-700 mb-1">
                            Subtitle *
                        </label>
                        <textarea
                            id="subtitle"
                            value={subtitle}
                            onChange={(e) => setSubtitle(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Don't worry, if you don't know for sure, just choose which one is closest now."
                            rows={2}
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Helper text shown below the question.
                        </p>
                    </div>

          <div>
            <label htmlFor="ocdProfileKey" className="block text-sm font-medium text-gray-700 mb-1">
              OCD Profile Key *
            </label>
            <input
              type="text"
              id="ocdProfileKey"
              value={ocdProfileKey}
              onChange={(e) => setOcdProfileKey(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., onboarding_most_distressing_theme"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Lowercase letters, numbers, and underscores only.
            </p>
          </div>
                </form>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        loading={isSaving}
                        disabled={isSaving}
                    >
                        {isNew ? 'Add Template' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

